import { useMemo } from "react"

interface CalendarDay {
  date: string
  status: "booked" | "available" | "blocked"
  booking_id?: string
  rate?: number
}

interface OccupancyCalendarProps {
  days: CalendarDay[]
  month: string // "YYYY-MM" format
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]

export function OccupancyCalendar({ days, month }: OccupancyCalendarProps) {
  const { weeks, monthLabel } = useMemo(() => {
    const [year, m] = month.split("-").map(Number)
    const label = new Date(year, m - 1, 1).toLocaleDateString(undefined, {
      month: "long",
      year: "numeric",
    })

    const firstDay = new Date(year, m - 1, 1)
    const lastDay = new Date(year, m, 0)
    const daysInMonth = lastDay.getDate()

    // Monday = 0, Sunday = 6
    let startDow = firstDay.getDay() - 1
    if (startDow < 0) startDow = 6

    const dayMap = new Map<string, CalendarDay>()
    for (const d of days) {
      dayMap.set(d.date, d)
    }

    const cells: Array<{ day: number | null; data: CalendarDay | null }> = []

    // Leading empty cells
    for (let i = 0; i < startDow; i++) {
      cells.push({ day: null, data: null })
    }

    // Actual days
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`
      cells.push({ day: d, data: dayMap.get(dateStr) || null })
    }

    // Trailing empty cells to fill last week
    while (cells.length % 7 !== 0) {
      cells.push({ day: null, data: null })
    }

    const weekRows: typeof cells[] = []
    for (let i = 0; i < cells.length; i += 7) {
      weekRows.push(cells.slice(i, i + 7))
    }

    return { weeks: weekRows, monthLabel: label }
  }, [days, month])

  return (
    <div className="occ-calendar">
      <div className="occ-calendar__title">{monthLabel}</div>
      <div className="occ-calendar__grid">
        {WEEKDAYS.map(w => (
          <div key={w} className="occ-calendar__weekday">{w}</div>
        ))}
        {weeks.flat().map((cell, i) => {
          if (cell.day === null) {
            return <div key={`empty-${i}`} className="occ-calendar__cell occ-calendar__cell--empty" />
          }
          const status = cell.data?.status || "available"
          const today = new Date().toISOString().slice(0, 10)
          const cellDate = `${month}-${String(cell.day).padStart(2, "0")}`
          const isToday = cellDate === today
          return (
            <div
              key={cellDate}
              className={`occ-calendar__cell occ-calendar__cell--${status}${isToday ? " occ-calendar__cell--today" : ""}`}
              title={`${cellDate}: ${status}`}
            >
              <span className="occ-calendar__day-num">{cell.day}</span>
            </div>
          )
        })}
      </div>
      <div className="occ-calendar__legend">
        <span className="occ-calendar__legend-item">
          <span className="occ-calendar__legend-dot occ-calendar__legend-dot--booked" />
          Booked
        </span>
        <span className="occ-calendar__legend-item">
          <span className="occ-calendar__legend-dot occ-calendar__legend-dot--available" />
          Available
        </span>
        <span className="occ-calendar__legend-item">
          <span className="occ-calendar__legend-dot occ-calendar__legend-dot--blocked" />
          Blocked
        </span>
      </div>
    </div>
  )
}
